<template>
  <div class="recruitment-candidates-page">
    <QHeader class="bg-white text-primary shadow-soft">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goToDashboard()" />
        <QToolbarTitle class="toolbar-subtitled">
          {{ headerTitle }}
          <p>{{ headerSubtitle }}</p>
        </QToolbarTitle>

        <QBtn flat icon="icon-f-more-vertical">
          <QMenu content-class="recruitment-candidates-tabs-menu">
            <QList>
              <MenuItem
                v-if="shouldDisplayReportProblem"
                icon="icon-f-alert-triangle"
                :label="$filters.whitespacedPunctuation($t('ETAB.PAGES.RECRUITMENT.@REPORT_PROBLEM'))"
                @on-action="onReportProblem"
              />
              <MenuItem v-if="shouldDisplayCancel" icon="icon-f-x" :label="$t('ETAB.PAGES.RECRUITMENT.@CANCEL_DEMAND')" @on-action="onCancelDemand" />
            </QList>
          </QMenu>
        </QBtn>
      </QToolbar>
      <QTabs v-if="shouldDisplayTabs" align="justify">
        <QRouteTab no-caps :to="appliedRoute" :label="appliedTabTitle" />
        <QRouteTab no-caps :to="selectedRoute" :label="selectedTabTitle" />
      </QTabs>
    </QHeader>

    <QPageContainer>
      <QPage class="recruitment-candidates-list">
        <QPullToRefresh no-mouse @refresh="refreshRecruitment">
          <router-view />
        </QPullToRefresh>
      </QPage>

      <QPageSticky v-if="$q.screen.gt.xs" position="bottom-right" :offset="[10, 8]">
        <QBtn fab icon="icon-f-rotate-cw" color="secondary" @click="refreshRecruitment" />
      </QPageSticky>
    </QPageContainer>

    <CancelAEHelperModal v-model:opened="cancelAEHelperOpened" />
  </div>
</template>

<script>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ContactManager } from "@core/managers/contact.manager";
  import { RecruitmentsService } from "@core/services";
  import { ETAB_ROUTES } from "@etab/config";
  import { GETTER_RECRUITMENT_GET_CURRENT, ACTION_RECRUITMENT_GET } from "@etab/stores/types";
  import { GETTER_TRANSITIONS_GET } from "@core/store/types";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useCssTransitionsStore } from "@core/store/modules/cssTransitions";
  import { mapStores } from "pinia";

  import MenuItem from "@core/components/common/MenuItem.functional.vue";
  import CancelAEHelperModal from "@etab/components/recruitments/detail/CancelAEHelper.modal.vue";

  export default {
    components: {
      MenuItem,
      CancelAEHelperModal,
    },
    data() {
      return {
        cancelAEHelperOpened: false,
        headerTitle: "",
        headerSubtitle: "",
      };
    },
    computed: {
      ...mapStores(useRecruitmentStore, useCssTransitionsStore),
      demandId() {
        return this.$route.params.demandId;
      },
      demandType() {
        return this.$route.params.demandType;
      },
      recruitment() {
        return this.recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
      },
      appliedRoute() {
        return {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.applied.name,
          params: { demandId: this.demandId, demandType: this.demandType },
        };
      },
      selectedRoute() {
        return {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.selected.name,
          params: { demandId: this.demandId, demandType: this.demandType },
        };
      },
      shouldDisplayReportProblem() {
        return !this.recruitment.isArchived();
      },
      shouldDisplayCancel() {
        return !this.recruitment.hasStarted();
      },
      shouldDisplayTabs() {
        return !this.recruitment.isFilled();
      },
      selectedTabTitle() {
        return this.$tc(`ETAB.PAGES.RECRUITMENT.@CHOSEN_TAB_SELECTED`, this.selectedCandidatesAmount);
      },
      appliedTabTitle() {
        // RG: Recruitment Candidates - if expired, do not show the applied candidates amount
        return this.$tc("ETAB.PAGES.RECRUITMENT.@CANDIDATES_TAB", this.recruitment.getAppliedCandidatesAmount());
      },
      transition() {
        return this.cssTransitionsStore[GETTER_TRANSITIONS_GET];
      },
      canceledCandidatesAmount() {
        return this.recruitment.getCanceledCandidates().length;
      },
      selectedCandidatesAmount() {
        return this.recruitment.getSelectedCandidates().length;
      },
      appliedCandidatesAmount() {
        return this.recruitment.getCurrentCandidates().length;
      },
    },
    watch: {
      // we watch any change in the url (could be navigation from notification, favorite etc.)
      async demandId() {
        await this.refreshRecruitment();
        this.inializeTitles();
      },
    },
    created() {
      // if we come directly to the parent route, we redirect to the right child
      if (this.$route.name === ETAB_ROUTES.dashboard.recruitment.temporary.detail.root.name) {
        this.redirect();
      }
    },
    mounted() {
      this.$_stopGlobalLoading();
      this.inializeTitles();
    },
    methods: {
      inializeTitles() {
        let headerTitle = "";
        let headerSubtitle = "";

        const qualification = this.recruitment.getQualificationLabel();
        headerSubtitle = this.$t("ETAB.PAGES.RECRUITMENT.@SUBTITLE_QUALIFICATION", { qualification });

        if (this.recruitment.isAE()) {
          const number = this.recruitment.getExtraRequiredAmount();
          const start = this.recruitment.getStartDate().format("ddd DD/MM");

          if (this.recruitment.hasStarted()) {
            headerTitle = qualification;
          } else {
            headerTitle = `${this.$tc("ETAB.PAGES.RECRUITMENT.@TITLE_AE_NUMBER", number)} - ${qualification}`;
          }

          if (this.recruitment.isOneDay()) {
            headerSubtitle = this.$t("ETAB.PAGES.RECRUITMENT.@SUBTITLE_DATES_ONE_DAY", { start });
          } else {
            const end = this.recruitment.getEndDate().format("DD/MM");
            headerSubtitle = this.$t("ETAB.PAGES.RECRUITMENT.@SUBTITLE_DATES", { start, end });
          }
        }

        this.headerTitle = headerTitle;
        this.headerSubtitle = headerSubtitle;
      },
      redirect() {
        if (this.recruitment.isFilled()) {
          // if the recruitment is filled we go directly to the selected tab
          this.redirectTo(this.selectedRoute);
          return;
        }
        if ((this.recruitment.isExpired() || this.recruitment.hasStarted()) && this.selectedCandidatesAmount > 0) {
          // if the recruitment is expired or started and we selected someone, we go directly to the selected tab
          this.redirectTo(this.selectedRoute);
          return;
        }
        if (this.canceledCandidatesAmount > 0) {
          // if the recruitment has a canceled Extra we go directly to the selected tab to check on it
          this.redirectTo(this.selectedRoute);
          return;
        }
        if (this.appliedCandidatesAmount === 0 && this.selectedCandidatesAmount > 0) {
          // if there is no candidates but a selected extra, go to the selected tab
          this.redirectTo(this.selectedRoute);
          return;
        }
        if (this.$route.name === this.selectedRoute.name && this.selectedCandidatesAmount === 0 && this.appliedCandidatesAmount === 0) {
          // if we are on the selected tab and there is no candidates, we don't move
          return;
        }
        // otherwise applied candidates is the default tab
        this.redirectTo(this.appliedRoute);
      },
      redirectTo(route) {
        if (this.$route.name !== route.name) {
          this.$router.replace(route);
        }
      },
      async refreshRecruitment(refresherDone) {
        const isFromPullToRefresh = typeof refresherDone === "function";
        try {
          if (!isFromPullToRefresh) {
            this.$_startGlobalLoading();
          }
          await this.recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: this.demandId, demandType: this.demandType });

          // let's be sure to not stay stuck on the wrong tab on edgy cases
          if (!this.shouldDisplayTabs) {
            this.redirect();
          }
        } catch (error) {
          NotificationsManager.error();
          throw error;
        } finally {
          this.$_stopGlobalLoading(); // in case there's a loading
          if (isFromPullToRefresh) {
            refresherDone();
          }
        }
      },
      openCancelAEHelperModal() {
        this.cancelAEHelperOpened = true;
      },
      goToDashboard() {
        if (this.recruitment.hasStarted()) {
          this.$router.push({ name: ETAB_ROUTES.dashboard.tabs.inProgress.name }).catch((suppressedError) => {});
        } else {
          this.$router.push({ name: ETAB_ROUTES.dashboard.tabs.upcoming.name }).catch((suppressedError) => {});
        }
      },
      onReportProblem() {
        const subject = this.$t("EMAILS.@DEMAND_PROBLEM", { demandId: this.demandId });
        ContactManager.contactEtabSupport({ subject });
      },
      async cancelDemand() {
        try {
          this.$_startGlobalLoading();
          if (this.recruitment.isAE()) {
            await RecruitmentsService.cancelAEDemand({ missionId: this.recruitment.getMissionId() });
          }
          // return to dashboard, the loading will stop there
          this.goToDashboard();
        } catch (error) {
          // we stop loading in case of problem
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        }
      },
      async onCancelDemand() {
        // for AE you need to cancel candidate job before canceling demand
        if (this.recruitment.isAE() && !this.recruitment.canBeCanceled()) {
          this.openCancelAEHelperModal();
        } else {
          this.$q
            .dialog({
              title: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@TITLE"),
              message: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@MESSAGE"),
              ok: {
                label: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@CONFIRM"),
                color: "secondary",
                noCaps: true,
              },
              cancel: {
                label: this.$t("ETAB.PAGES.RECRUITMENT.CANCEL_DIALOG.@CLOSE"),
                flat: true,
                noCaps: true,
              },
            })
            .onOk(() => this.cancelDemand());
        }
      },
    },
  };
</script>
